/**
 * Dashboard → Models: derived rules, parent posteriors, model fuse status + chart.
 */
import React, { useState } from 'react';
import TrueProbCiChart from './TrueProbCiChart';
import { useIngestModelMarketObservations } from '../api/hooks';
import { useAuth } from '../auth/AuthContext';

const BODY_FONT = '0.875rem';
const LABEL_FONT = '0.8125rem';
const SECTION_LABEL_FONT = '0.75rem';

function fmtNum(v, digits = 4) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toFixed(digits);
}

function fmtPct(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(2)}%`;
}

/**
 * Inline entry for a leg the fuse may stand on but whose own market will not publish — a QB
 * rushing line suppressed under ``min_offered_line``, or an anytime-TD market books never post
 * for QBs. Only rendered for legs whose YAML spec carries ``accept_model_applied``.
 *
 * Deliberately NOT a gate override: the leg blocks until a human types a number, and what the
 * fuse then stands on is that number. ``manual_entry_line`` pins the line where the question has
 * only one sensible one (anytime TD is "scores a TD" at 0.5, so the trader supplies a price and
 * nothing else); when it is null the trader supplies both.
 *
 * R is deliberately omitted — the writer defaults it to 0.02 when a row carries no R/P.
 */
function ManualLegEntry({ parent, fixtureId, sport, league, userEmail }) {
  const pinnedLine = parent.manual_entry_line;
  // Prefill the line where we know it: pinned by config, else whatever the leg currently sits
  // at. On a re-validation after drift that is the line books have moved to, so the trader
  // confirms a price rather than retyping a line they did not change.
  const seedLine = pinnedLine != null ? pinnedLine : parent.base_line;
  const [line, setLine] = useState(seedLine != null ? String(seedLine) : '');
  const [price, setPrice] = useState('');
  const [err, setErr] = useState(null);
  const ingest = useIngestModelMarketObservations();

  const lineNum = Number(line);
  const priceNum = Number(price);
  const valid =
    Number.isFinite(lineNum) &&
    line !== '' &&
    Number.isFinite(priceNum) &&
    priceNum > 0 &&
    priceNum < 1;

  const submit = async () => {
    setErr(null);
    try {
      await ingest.mutateAsync({
        fixture_id: fixtureId,
        market_id: parent.market_id,
        sport,
        league,
        // Provenance: the Parents table and true_odds.model_market_observations both show who
        // stood behind this number, rather than the "anonymous" the writer would default to.
        model_name: `manual:${userEmail || 'dashboard'}`,
        apply_mode: 'reseed',
        // Keep the market's real books live. Applying a value normally MUTES the target's book
        // signals, which is right for an automated model that owns the price and wrong here: a
        // typed value is a stand-in for a market we cannot sell, so if books later post a line
        // this market can publish, they must be able to take it back.
        link_competitors: true,
        rows: [{ player_id: parent.player_id, line: lineNum, p_fair: priceNum }],
      });
      setPrice('');
    } catch (e) {
      setErr(String(e?.message || e));
    }
  };

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      <input
        type="number"
        step="0.5"
        value={line}
        disabled={pinnedLine != null}
        onChange={(e) => setLine(e.target.value)}
        placeholder="line"
        title={pinnedLine != null ? `Line is fixed at ${pinnedLine} for this market` : 'Line'}
        style={{ width: 62, fontSize: LABEL_FONT }}
      />
      <input
        type="number"
        step="0.01"
        min="0"
        max="1"
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        placeholder="p_over"
        title="Fair probability the line goes over (0-1)"
        style={{ width: 70, fontSize: LABEL_FONT }}
      />
      <button
        type="button"
        disabled={!valid || ingest.isPending}
        onClick={submit}
        style={{ fontSize: LABEL_FONT }}
      >
        {ingest.isPending ? '…' : 'Set'}
      </button>
      {err && <span style={{ color: '#f87171', fontSize: LABEL_FONT }}>{err}</span>}
    </div>
  );
}

function boolBadge(on, yes = 'yes', no = 'no') {
  return on ? (
    <span className="badge badge-success">{yes}</span>
  ) : (
    <span className="badge" style={{ backgroundColor: '#475569', color: '#E2E8F0' }}>
      {no}
    </span>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="card chart-card full-width derived-model-section">
      <h3 style={{ marginTop: 0, marginBottom: subtitle ? '0.25rem' : '0.75rem' }}>{title}</h3>
      {subtitle ? (
        <p
          className="risk-card-lede risk-card-lede--compact"
          style={{ marginBottom: '0.65rem', fontSize: BODY_FONT }}
        >
          {subtitle}
        </p>
      ) : null}
      {children}
    </div>
  );
}

const marketStatusChipStyle = {
  flex: '1 1 auto',
  minWidth: '7.5rem',
  padding: '0.45rem 0.6rem',
  borderRadius: 8,
  background: 'rgba(15, 23, 42, 0.55)',
  border: '1px solid rgba(148, 163, 184, 0.22)',
};

function MarketStatusChip({ label, children }) {
  return (
    <div style={marketStatusChipStyle}>
      <div
        style={{
          fontSize: '0.68rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#94a3b8',
          marginBottom: '0.2rem',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: BODY_FONT, lineHeight: 1.3 }}>{children}</div>
    </div>
  );
}

function PluginDiagRow({ label, children }) {
  return (
    <div
      className="derived-plugin-diag-row"
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(7rem, 9rem) 1fr',
        gap: '0.5rem 0.75rem',
        alignItems: 'baseline',
        fontSize: BODY_FONT,
        lineHeight: 1.35,
        padding: '0.35rem 0',
        borderBottom: '1px solid rgba(148, 163, 184, 0.12)',
      }}
    >
      <span style={{ color: '#94a3b8', fontSize: LABEL_FONT, fontWeight: 500 }}>{label}</span>
      <span style={{ color: '#e2e8f0', wordBreak: 'break-word' }}>{children}</span>
    </div>
  );
}

function PluginDiagMetric({ label, value }) {
  return (
    <span style={{ marginRight: '0.65rem', whiteSpace: 'nowrap' }}>
      <span style={{ color: '#94a3b8', fontSize: LABEL_FONT }}>{label} </span>
      <span style={{ color: '#f1f5f9' }}>{value}</span>
    </span>
  );
}

function fmtShortTs(iso) {
  if (!iso || typeof iso !== 'string') return '—';
  const m = iso.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})/);
  if (m) return `${m[1]} ${m[2]} UTC`;
  return iso.slice(0, 19);
}

function fmtProbCiRow(p) {
  const lo = p.p_lower;
  const hi = p.p_upper;
  const z = p.ci_z;
  if (lo == null || hi == null || !Number.isFinite(Number(lo)) || !Number.isFinite(Number(hi))) {
    return '—';
  }
  const zPart = z != null && Number.isFinite(Number(z)) ? ` (z=${fmtNum(z, 2)})` : '';
  return `${fmtPct(lo)}–${fmtPct(hi)}${zPart}`;
}

function parentSpecsLabel(r) {
  if (Array.isArray(r.parent_specs) && r.parent_specs.length) {
    return r.parent_specs.map((s) => `${s.market_id}${s.role && s.role !== 'self' ? `:${s.role}` : ''}`).join(', ');
  }
  if (Array.isArray(r.parents)) return r.parents.join(', ');
  return '—';
}

export default function DerivedModelBreakdownPanel({
  isLoading,
  error,
  detail,
  booklessDerived = false,
  probChartProps = null,
}) {
  const { user } = useAuth();
  const modelUnavailable =
    detail?.derived_model_available === false || error?.response?.status === 404;

  if (isLoading) {
    return (
      <div className="card empty-state" style={{ padding: '2rem', textAlign: 'center' }}>
        Loading derived model…
      </div>
    );
  }

  if (modelUnavailable) {
    const msg =
      typeof detail?.message === 'string' && detail.message.trim()
        ? detail.message
        : 'No derived model is configured for this market, or no snapshot exists yet. Models breakdown applies to Sleeper derived lines (e.g. goals + assists, goalie FPTS) with YAML rules and parent markets.';
    return (
      <div
        className="card empty-state"
        style={{ padding: '2rem', textAlign: 'center', color: 'var(--sleeper-gray, #64748B)' }}
      >
        <p style={{ margin: 0 }}>{msg}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card empty-state" style={{ padding: '2rem', textAlign: 'center' }}>
        Could not load derived model detail.
      </div>
    );
  }

  if (!detail) {
    return null;
  }

  const rules = Array.isArray(detail.rules_config) ? detail.rules_config : [];
  const parents = Array.isArray(detail.parents) ? detail.parents : [];
  const tier1 = detail.derived_tier1_quote_block;
  const entriesEnabled = Boolean(detail.entries_kf_enabled);

  const live = detail.postgres_live || {};
  const mq = live.market_quotes_row;
  const lu = detail.layer_usage || {};

  const anyRuleRequestsMuteOnFuse = rules.some((r) => Boolean(r.mute_target_books_when_enabled));
  const modelHistory = Array.isArray(detail.derived_model_output_history)
    ? detail.derived_model_output_history
    : [];
  const modelDiag = detail.model_diagnostics || null;
  const inferredMl = modelDiag?.inferred_ml || null;
  const fptsDiag = modelDiag?.fpts || null;
  const lambdaDiag = modelDiag?.lambdas || null;

  const overviewSubtitle = [
    detail.snapshot_updated_at ? `Updated ${detail.snapshot_updated_at}` : null,
    detail.target_market_id ? `target ${detail.target_market_id}` : null,
    detail.league ? detail.league : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="derived-model-breakdown" style={{ fontSize: BODY_FONT }}>
      {booklessDerived && probChartProps ? (
        <Section
          title="True probability + line"
          subtitle="EntriesKF fair prob and balanced line; purple dots mark each model fuse."
        >
          <TrueProbCiChart {...probChartProps} />
        </Section>
      ) : null}

      <Section
        title="Model & fuse"
        subtitle={
          overviewSubtitle ||
          'Published layer, YAML rule, plugin diagnostics, and recent fuse outputs.'
        }
      >
        <div
          className="gate-bd-row derived-model-overview-row"
          style={{
            marginBottom: '0.65rem',
            flexWrap: 'wrap',
            gap: '0.35rem',
            fontSize: BODY_FONT,
          }}
        >
          <span className="gate-bd-label">Overview</span>
          <span className="gate-bd-detail">
            Entries KF {boolBadge(entriesEnabled)} · Tier‑1 block{' '}
            <span className={tier1 ? 'gate-bd-on' : 'gate-bd-off'}>{tier1 || '—'}</span>
          </span>
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '0.55rem',
            alignItems: 'stretch',
            marginBottom: '0.85rem',
          }}
        >
          <MarketStatusChip label="Published">
            {lu.published_quote_uses_entries_layer ? (
              <span className="badge badge-success">EntriesKF</span>
            ) : lu.published_quote_uses_base_kf_layer ? (
              <span className="badge" style={{ backgroundColor: '#ca8a04', color: '#0f172a' }}>
                Base KF
              </span>
            ) : (
              <span style={{ color: '#94a3b8' }}>—</span>
            )}
          </MarketStatusChip>
          <MarketStatusChip label="Competitor sync">
            {mq == null ? (
              <span style={{ color: '#94a3b8' }}>No row</span>
            ) : lu.competitor_sync_muted ? (
              <span className="badge" style={{ backgroundColor: '#b45309', color: '#fff8f0' }}>
                Muted
              </span>
            ) : (
              <span className="badge badge-success">Active</span>
            )}
          </MarketStatusChip>
          <MarketStatusChip label="Mute on fuse">
            {rules.length === 0 ? (
              <span style={{ color: '#94a3b8' }}>—</span>
            ) : (
              boolBadge(anyRuleRequestsMuteOnFuse, 'yes', 'no')
            )}
          </MarketStatusChip>
          <MarketStatusChip label="Parents ready">
            {boolBadge(Boolean(lu.parents_ready_for_model_fuse))}
          </MarketStatusChip>
          <MarketStatusChip label="Model flag">
            {boolBadge(Boolean(lu.entrieskf_derived_model_flag))}
          </MarketStatusChip>
          <MarketStatusChip label="Inputs OK">
            {boolBadge(Boolean(lu.model_inputs_available_now))}
          </MarketStatusChip>
        </div>

        {rules.length === 0 ? (
          <p className="risk-card-lede" style={{ margin: '0 0 0.75rem', fontSize: BODY_FONT }}>
            No rule objects in API config. Use a sport writer for full YAML.
          </p>
        ) : (
          <div className="scrollable-table-container compact" style={{ marginBottom: '0.85rem' }}>
            <table className="ob-table ob-table-compact" style={{ width: '100%', fontSize: BODY_FONT }}>
              <thead>
                <tr>
                  <th>rule_id</th>
                  <th>adapter</th>
                  <th>parents</th>
                  <th className="num">innov z² min</th>
                  <th className="num">debounce ms</th>
                  <th>bookless</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.rule_id || r.adapter}>
                    <td>{r.rule_id}</td>
                    <td>{r.adapter}</td>
                    <td style={{ maxWidth: 220, wordBreak: 'break-word' }}>{parentSpecsLabel(r)}</td>
                    <td className="num">{fmtNum(r.innovation_z2_min, 6)}</td>
                    <td className="num">{r.debounce_ms != null ? String(r.debounce_ms) : '—'}</td>
                    <td>{boolBadge(Boolean(r.allow_engine_without_target_books), 'allow', 'no')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {modelDiag ? (
          <div style={{ marginBottom: '0.85rem' }}>
            <div
              style={{
                fontSize: SECTION_LABEL_FONT,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: '#94a3b8',
                marginBottom: '0.45rem',
              }}
            >
              Plugin diagnostics
            </div>
            <div
              style={{
                background: 'rgba(15, 23, 42, 0.45)',
                border: '1px solid rgba(148, 163, 184, 0.18)',
                borderRadius: 8,
                padding: '0.35rem 0.65rem',
              }}
            >
              <PluginDiagRow label="Inferred 1X2">
                {inferredMl ? (
                  <>
                    <PluginDiagMetric label="Home" value={fmtPct(inferredMl.home)} />
                    <PluginDiagMetric label="Draw" value={fmtPct(inferredMl.draw)} />
                    <PluginDiagMetric label="Away" value={fmtPct(inferredMl.away)} />
                  </>
                ) : (
                  '—'
                )}
              </PluginDiagRow>
              {lambdaDiag ? (
                <PluginDiagRow label="λ (Poisson)">
                  <PluginDiagMetric label="Team" value={fmtNum(lambdaDiag.team_goals, 3)} />
                  <PluginDiagMetric label="Opp" value={fmtNum(lambdaDiag.opp_goals, 3)} />
                  <PluginDiagMetric label="1H opp" value={fmtNum(lambdaDiag.opp_goals_1h, 3)} />
                  <PluginDiagMetric label="Saves" value={fmtNum(lambdaDiag.saves, 3)} />
                  <PluginDiagMetric label="φ" value={fmtNum(lambdaDiag.phi, 3)} />
                </PluginDiagRow>
              ) : null}
              {fptsDiag ? (
                <PluginDiagRow label="FPTS distribution">
                  <PluginDiagMetric label="Mean" value={fmtNum(fptsDiag.mean, 2)} />
                  <PluginDiagMetric label="Median" value={fmtNum(fptsDiag.median, 2)} />
                  <PluginDiagMetric label="Balanced" value={fmtNum(fptsDiag.balanced_line, 1)} />
                  <PluginDiagMetric
                    label={`P(over @ ${fmtNum(fptsDiag.target_line, 1)})`}
                    value={fmtPct(fptsDiag.p_over_plugin)}
                  />
                </PluginDiagRow>
              ) : null}
            </div>
          </div>
        ) : null}

        <div
          style={{
            fontSize: SECTION_LABEL_FONT,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#94a3b8',
            marginBottom: '0.35rem',
          }}
        >
          Recent model outputs
        </div>
        {modelHistory.length === 0 ? (
          <p className="risk-card-lede" style={{ margin: 0, color: '#94a3b8', fontSize: BODY_FONT }}>
            No stored model updates yet for this market.
          </p>
        ) : (
          <div className="scrollable-table-container">
            <table className="ob-table ob-table-compact" style={{ width: '100%', fontSize: BODY_FONT }}>
              <thead>
                <tr>
                  <th>time (UTC)</th>
                  <th className="num">p obs</th>
                  <th className="num">z²</th>
                  <th className="num">p before</th>
                  <th className="num">p after</th>
                  <th className="num">y</th>
                  <th className="num">R</th>
                </tr>
              </thead>
              <tbody>
                {modelHistory.map((row, idx) => (
                  <tr key={row.timestamp || `h-${idx}`}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtShortTs(row.timestamp)}</td>
                    <td className="num">{fmtPct(row.p_fair_obs)}</td>
                    <td className="num">{fmtNum(row.z2_innovation, 4)}</td>
                    <td className="num">{fmtPct(row.p_fair_before)}</td>
                    <td className="num">{fmtPct(row.p_fair_after)}</td>
                    <td className="num">{fmtNum(row.y_observation)}</td>
                    <td className="num">{fmtNum(row.R_observation, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Parents" subtitle="Base KF posteriors; p / CI match writer KF (±z·√P in logit).">
        <div className="scrollable-table-container">
          <table className="ob-table ob-table-compact" style={{ width: '100%', fontSize: BODY_FONT }}>
            <thead>
              <tr>
                <th>market_id</th>
                <th>role</th>
                <th>player</th>
                <th>n</th>
                <th>min</th>
                <th>post</th>
                <th>fuse</th>
                <th>source</th>
                <th>block</th>
                <th className="num">p</th>
                <th className="num">CI</th>
              </tr>
            </thead>
            <tbody>
              {parents.map((p) => (
                <tr key={p.market_key || `${p.market_id}-${p.role || 'self'}`}>
                  <td style={{ maxWidth: 140, wordBreak: 'break-word' }}>{p.market_id}</td>
                  <td>{p.role || 'self'}</td>
                  <td style={{ maxWidth: 100, wordBreak: 'break-word' }}>{p.player_id || '—'}</td>
                  <td>{p.n_books ?? '—'}</td>
                  <td>{p.min_books_for_quote ?? '—'}</td>
                  <td>{boolBadge(Boolean(p.has_posterior))}</td>
                  <td>{boolBadge(Boolean(p.ready_for_derived_fuse))}</td>
                  {/*
                    ``source`` and ``block`` answer different questions and a manual leg is the
                    case where they disagree: the leg is fuse-ready while its own quote gate says
                    no. Showing the gate reason alongside the manual badge is the point — a QB
                    priced off a hand-entered rushing line must never look identical to one
                    priced off books.
                  */}
                  <td>
                    {p.source === 'manual' ? (
                      <span
                        className="badge"
                        style={{ backgroundColor: '#7c3aed', color: '#F5F3FF' }}
                        title={p.parent_quote_gate_reason || 'Standing on a trader-entered value'}
                      >
                        manual
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>book</span>
                    )}
                  </td>
                  <td
                    style={{
                      maxWidth: 220,
                      color: p.fuse_block_reason ? '#f87171' : '#94a3b8',
                    }}
                  >
                    {p.fuse_block_reason || '—'}
                    {/* Same note as the Operations page: a Stale leg needs a NEW value, which is
                        not obvious from a drift figure alone. */}
                    {p.accept_model_applied && !p.ready_for_derived_fuse && p.player_id && (
                      <div style={{ marginTop: '0.2rem', color: '#93c5fd', fontSize: LABEL_FONT }}>
                        {String(p.fuse_block_reason || '').includes('stale')
                          ? 'Enter a new value to reopen — the earlier one no longer applies.'
                          : 'Enter a value to open this market.'}
                      </div>
                    )}
                    {p.accept_model_applied && !p.ready_for_derived_fuse && p.player_id && (
                      <ManualLegEntry
                        parent={p}
                        fixtureId={detail?.fixture_id}
                        sport={detail?.sport}
                        league={detail?.league}
                        userEmail={user?.email}
                      />
                    )}
                  </td>
                  <td className="num">{fmtPct(p.p_fair)}</td>
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    {fmtProbCiRow(p)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
